import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/contexts/LanguageContext";
import { 
  Sparkles, RefreshCw, Plus, X, 
  Users, Heart, Truck, MessageCircle, 
  DollarSign, Key, Cog, Handshake, Wallet 
} from "lucide-react";
// animations removed
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BMCBlock = ({ title, icon: Icon, items, color, bgColor, onAdd, onRemove, addPlaceholder }) => {
  const [newItem, setNewItem] = useState("");

  const handleAdd = () => {
    if (newItem.trim()) {
      onAdd(newItem.trim());
      setNewItem("");
    }
  };

  return (
    <div className={`rounded-xl border ${color} ${bgColor} p-4 flex flex-col min-h-[160px]`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-stone-600" />
        <span className="text-xs font-semibold text-stone-600 uppercase tracking-wide">
          {title}
        </span>
      </div>
      <div className="flex-1 space-y-1.5">
        {items?.map((item, index) => (
          <div
            key={index}
            className="bg-white/80 px-2 py-1.5 rounded text-xs text-stone-700 flex items-center justify-between group"
          >
            <span className="flex-1 whitespace-pre-line text-xs leading-snug break-words">{item}</span>
            <button
              onClick={() => onRemove(index)}
              className="opacity-0 group-hover:opacity-100 transition-opacity ml-1 p-0.5 hover:bg-red-100 rounded"
            >
              <X className="w-3 h-3 text-red-500" />
            </button>
          </div>
        ))}
      </div>
      <div className="mt-2 flex gap-1">
        <Input
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          placeholder={addPlaceholder}
          className="text-xs h-7"
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
        />
        <Button size="sm" variant="ghost" onClick={handleAdd} className="h-7 px-2">
          <Plus className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
};

const BusinessModelStep = ({ project, setProject, saveProject, goToStep }) => {
  const { t, language } = useLanguage();
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [bmc, setBmc] = useState({
    customer_segments: [],
    value_propositions: [],
    channels: [],
    customer_relationships: [],
    revenue_streams: [],
    key_resources: [],
    key_activities: [],
    key_partners: [],
    cost_structure: [],
  });

  useEffect(() => {
    if (project?.business_model) {
      setBmc(project.business_model);
      setHasGenerated(true);
    }
  }, []);

  const generateBMC = async () => {
    if (!project?.value_proposition) {
      toast.error(t('common_error'));
      return;
    }

    setIsGenerating(true);
    try {
      const response = await fetch(`${API}/generate/business-model`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          problem_statement: project.problem_statement,
          selected_idea: project.selected_idea,
          concept: project.concept,
          value_proposition: project.value_proposition,
          language,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setBmc(data);
        setHasGenerated(true);
        
        await saveProject({ 
          business_model: data,
          current_step: 4
        }, false);
        
        setProject(prev => ({ ...prev, business_model: data }));
        toast.success(t('common_success'));
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common_error'));
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error(t('common_error'));
    } finally {
      setIsGenerating(false);
    }
  };

  const updateBlock = (block, items) => {
    setBmc(prev => ({ ...prev, [block]: items }));
  };

  const addItem = (block, item) => {
    const items = [...(bmc[block] || []), item];
    updateBlock(block, items);
  };

  const removeItem = (block, index) => {
    const items = bmc[block]?.filter((_, i) => i !== index) || [];
    updateBlock(block, items);
  };

  const handleSave = async () => {
    await saveProject({ 
      business_model: bmc,
      current_step: 5
    });
    setProject(prev => ({ ...prev, business_model: bmc }));
  };

  const handleContinue = async () => {
    await handleSave();
    goToStep(6);
  };

  const blocks = [
    { key: "key_partners", title: t('bmc_partners'), icon: Handshake, color: "border-purple-200", bgColor: "bg-purple-50" },
    { key: "key_activities", title: t('bmc_activities'), icon: Cog, color: "border-blue-200", bgColor: "bg-blue-50" },
    { key: "key_resources", title: t('bmc_resources'), icon: Key, color: "border-indigo-200", bgColor: "bg-indigo-50" },
    { key: "value_propositions", title: t('bmc_value_props'), icon: Heart, color: "border-primary/30", bgColor: "bg-primary-light" },
    { key: "customer_relationships", title: t('bmc_relationships'), icon: MessageCircle, color: "border-pink-200", bgColor: "bg-pink-50" },
    { key: "channels", title: t('bmc_channels'), icon: Truck, color: "border-cyan-200", bgColor: "bg-cyan-50" },
    { key: "customer_segments", title: t('bmc_segments'), icon: Users, color: "border-green-200", bgColor: "bg-green-50" },
    { key: "cost_structure", title: t('bmc_costs'), icon: Wallet, color: "border-red-200", bgColor: "bg-red-50" },
    { key: "revenue_streams", title: t('bmc_revenue'), icon: DollarSign, color: "border-emerald-200", bgColor: "bg-emerald-50" },
  ];

  return (
    <div className="h-full flex flex-col overflow-auto">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 px-4 sm:px-8 py-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="font-heading text-2xl font-semibold text-stone-900">
              {t('bmc_title')}
            </h2>
            <p className="text-stone-600">
              {t('bmc_description')}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={generateBMC}
              disabled={isGenerating}
              variant={hasGenerated ? "outline" : "default"}
              className={hasGenerated ? "btn-secondary" : "btn-primary"}
              data-testid="generate-bmc-btn"
            >
              {isGenerating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  {t('common_generating')}
                </>
              ) : hasGenerated ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {t('bmc_regenerate')}
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  {t('bmc_generate')}
                </>
              )}
            </Button>
            {hasGenerated && (
              <Button
                onClick={handleContinue}
                className="btn-primary"
                data-testid="continue-to-concept-btn"
              >
                {t('bmc_continue')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Canvas */}
      <div className="flex-1 p-4 sm:p-8">
        <div className="max-w-7xl mx-auto">
          {!hasGenerated ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-accent-purple rounded-full flex items-center justify-center mx-auto mb-6">
                <Cog className="w-10 h-10 text-purple-600" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-stone-900 mb-2">
                {t('bmc_generate')}
              </h3>
              <p className="text-stone-600 mb-6 max-w-md mx-auto">
                {t('bmc_description')}
              </p>
              <Button
                onClick={generateBMC}
                disabled={isGenerating}
                className="btn-primary"
                data-testid="generate-bmc-empty-btn"
              >
                {isGenerating ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    {t('common_generating')}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    {t('bmc_generate')}
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div>
              {/* BMC Grid Layout */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                {/* Top row */}
                <div className="md:col-span-1 md:row-span-2">
                  <BMCBlock
                    {...blocks[0]}
                    items={bmc.key_partners}
                    onAdd={(item) => addItem("key_partners", item)}
                    onRemove={(idx) => removeItem("key_partners", idx)}
                    addPlaceholder={t('common_add')}
                  />
                </div>
                <div className="md:col-span-1">
                  <BMCBlock
                    {...blocks[1]}
                    items={bmc.key_activities}
                    onAdd={(item) => addItem("key_activities", item)}
                    onRemove={(idx) => removeItem("key_activities", idx)}
                    addPlaceholder={t('common_add')}
                  />
                </div>
                <div className="md:col-span-1 md:row-span-2">
                  <BMCBlock
                    {...blocks[3]}
                    items={bmc.value_propositions}
                    onAdd={(item) => addItem("value_propositions", item)}
                    onRemove={(idx) => removeItem("value_propositions", idx)}
                    addPlaceholder={t('common_add')}
                  />
                </div>
                <div className="md:col-span-1">
                  <BMCBlock
                    {...blocks[4]}
                    items={bmc.customer_relationships}
                    onAdd={(item) => addItem("customer_relationships", item)}
                    onRemove={(idx) => removeItem("customer_relationships", idx)}
                    addPlaceholder={t('common_add')}
                  />
                </div>
                <div className="md:col-span-1 md:row-span-2">
                  <BMCBlock
                    {...blocks[6]}
                    items={bmc.customer_segments}
                    onAdd={(item) => addItem("customer_segments", item)}
                    onRemove={(idx) => removeItem("customer_segments", idx)}
                    addPlaceholder={t('common_add')}
                  />
                </div>
                
                {/* Second row */}
                <div className="md:col-span-1">
                  <BMCBlock
                    {...blocks[2]}
                    items={bmc.key_resources}
                    onAdd={(item) => addItem("key_resources", item)}
                    onRemove={(idx) => removeItem("key_resources", idx)}
                    addPlaceholder={t('common_add')}
                  />
                </div>
                <div className="md:col-span-1">
                  <BMCBlock
                    {...blocks[5]}
                    items={bmc.channels}
                    onAdd={(item) => addItem("channels", item)}
                    onRemove={(idx) => removeItem("channels", idx)}
                    addPlaceholder={t('common_add')}
                  />
                </div>

                {/* Bottom row */}
                <div className="md:col-span-2">
                  <BMCBlock
                    {...blocks[7]}
                    items={bmc.cost_structure}
                    onAdd={(item) => addItem("cost_structure", item)}
                    onRemove={(idx) => removeItem("cost_structure", idx)}
                    addPlaceholder={t('common_add')}
                  />
                </div>
                <div className="md:col-span-3">
                  <BMCBlock
                    {...blocks[8]}
                    items={bmc.revenue_streams}
                    onAdd={(item) => addItem("revenue_streams", item)}
                    onRemove={(idx) => removeItem("revenue_streams", idx)}
                    addPlaceholder={t('common_add')}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessModelStep;
